import { Users, TrendingUp, Clock, HardDrive, Mic2, Music, ExternalLink, AlertCircle, X, WifiOff, Gift, Copy, Check, SkipForward, RefreshCw, Square, Play, Key } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { BACKEND_URL } from "../../context/AuthContext";

export const OverviewTab = ({
  stats, listenerHistory, currentUser, navigate,
  handleGoLive, setShowUploadMusicModal,
  showUpgradeModal, setShowUpgradeModal,
  refreshStats
}) => {
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaArtist, setMetaArtist] = useState("");
  const [serviceStatus, setServiceStatus] = useState(null);
  const [serviceLoading, setServiceLoading] = useState({});

  const fetchServiceStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/dashboard/station-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setServiceStatus(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchServiceStatus();
    const interval = setInterval(fetchServiceStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchServiceStatus]);

  useEffect(() => {
    if (currentUser?.user_id) {
      fetch(`${BACKEND_URL}/api/referral/stats?user_id=${currentUser.user_id}`)
        .then(r => r.json())
        .then(setReferralData)
        .catch(() => {});
    }
  }, [currentUser?.user_id]);

  const copyReferralLink = () => {
    if (referralData?.referral_code) {
      navigator.clipboard.writeText(`${window.location.origin}?ref=${referralData.referral_code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSkipSong = async () => {
    setSkipping(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/dashboard/skip-song`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setActionMsg({ type: "success", text: data.message || "Song skipped!" });
      if (refreshStats) setTimeout(refreshStats, 1500);
    } catch {
      setActionMsg({ type: "error", text: "Failed to skip song" });
    }
    setSkipping(false);
    setTimeout(() => setActionMsg(null), 3000);
  };

  const handleUpdateMetadata = async () => {
    if (!metaTitle.trim()) return;
    setUpdating(true);
    setActionMsg(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/dashboard/update-metadata`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ title: metaTitle.trim(), artist: metaArtist.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setActionMsg({ type: "success", text: data.message || "Metadata updated!" });
        setShowMetadataModal(false);
        setMetaTitle("");
        setMetaArtist("");
        if (refreshStats) setTimeout(refreshStats, 1500);
      } else {
        setActionMsg({ type: "error", text: data.detail || "Failed to update metadata" });
      }
    } catch {
      setActionMsg({ type: "error", text: "Failed to update metadata" });
    }
    setUpdating(false);
    setTimeout(() => setActionMsg(null), 3000);
  };

  const formatTime = (s) => {
    if (!s || s <= 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleServiceAction = async (service, action) => {
    const key = `${service}_${action}`;
    setServiceLoading(prev => ({ ...prev, [key]: true }));
    setActionMsg(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BACKEND_URL}/api/dashboard/service/${service}/${action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setActionMsg({ type: "success", text: data.message || `Service ${action}ed` });
        setTimeout(fetchServiceStatus, 1000);
        if (refreshStats) setTimeout(refreshStats, 2000);
      } else {
        setActionMsg({ type: "error", text: data.detail || `Failed to ${action} service` });
      }
    } catch {
      setActionMsg({ type: "error", text: `Failed to ${action} service` });
    }
    setServiceLoading(prev => ({ ...prev, [key]: false }));
    setTimeout(() => setActionMsg(null), 3000);
  };

  return (
  <div className="space-y-6">
    {/* AzuraCast Connection Error Banner */}
    {stats?.azuracast_error && (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="azuracast-error-banner">
        <WifiOff className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-semibold text-sm">AzuraCast Connection Issue</p>
          <p className="text-red-300/70 text-xs mt-1">{stats.azuracast_error}</p>
          <p className="text-white/40 text-xs mt-2">Check that AZURACAST_URL and AZURACAST_API_KEY are set correctly in your server environment variables.</p>
        </div>
      </div>
    )}
    {/* Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="stat-listeners">
        <div className="flex items-center justify-between mb-4">
          <Users className="w-8 h-8 text-purple-400" />
          <span className="text-green-400 text-sm">Live</span>
        </div>
        <p className="text-3xl font-bold text-white">{stats?.listeners || 0}</p>
        <p className="text-white/50 text-sm mt-1">Current Listeners</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="stat-peak">
        <div className="flex items-center justify-between mb-4">
          <TrendingUp className="w-8 h-8 text-pink-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.peak_listeners || 0}</p>
        <p className="text-white/50 text-sm mt-1">Peak Listeners</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="stat-uptime">
        <div className="flex items-center justify-between mb-4">
          <Clock className="w-8 h-8 text-green-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.uptime || '99.9%'}</p>
        <p className="text-white/50 text-sm mt-1">Uptime</p>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="stat-storage">
        <div className="flex items-center justify-between mb-4">
          <HardDrive className="w-8 h-8 text-blue-400" />
        </div>
        <p className="text-3xl font-bold text-white">{stats?.storage_used || '0 GB'}</p>
        <p className="text-white/50 text-sm mt-1">of {stats?.storage_limit || '5 GB'} used</p>
      </div>
    </div>

    {/* Listener Chart */}
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="listener-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Listener History</h3>
        <span className="text-xs text-white/40 flex items-center gap-1">
          <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
          Real-time
        </span>
      </div>
      <div className="h-48 flex items-end gap-1">
        {listenerHistory.length > 0 ? (
          (() => {
            const maxListeners = Math.max(...listenerHistory.map(h => h.listeners), 10);
            const colors = [
              'from-purple-600 to-purple-400', 'from-pink-600 to-pink-400',
              'from-blue-600 to-blue-400', 'from-cyan-600 to-cyan-400',
              'from-green-600 to-green-400', 'from-yellow-600 to-yellow-400',
              'from-orange-600 to-orange-400', 'from-red-600 to-red-400',
            ];
            return listenerHistory.map((item, i) => {
              const pct = item.listeners > 0 ? Math.max(8, (item.listeners / maxListeners) * 100) : 4;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end group cursor-pointer" style={{ height: '100%' }}>
                  <span className="text-xs text-purple-400 opacity-0 group-hover:opacity-100 transition font-bold mb-1">{item.listeners}</span>
                  <div className={`w-full rounded-t transition-all duration-500 bg-gradient-to-t ${colors[i % colors.length]} shadow-lg hover:scale-105`}
                    style={{ height: `${pct}%`, minHeight: '4px', opacity: item.listeners > 0 ? 1 : 0.3, flexShrink: 0 }} />
                  <span className="text-white/40 text-xs mt-1 flex-shrink-0">{item.time}</span>
                </div>
              );
            });
          })()
        ) : (
          <div className="flex-1 flex items-center justify-center text-white/40">No listener data available</div>
        )}
      </div>
    </div>

    {/* Now Playing & Playing Next */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="now-playing-card">
        <div className="flex items-center gap-2 mb-4">
          <Music className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Now Playing</h3>
        </div>
        {stats?.now_playing ? (
          <div>
            <div className="flex items-center gap-4 mb-3">
              {stats.now_playing.art && <img src={stats.now_playing.art} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />}
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{stats.now_playing.title || 'Unknown Track'}</p>
                <p className="text-white/50 text-sm truncate">{stats.now_playing.artist || 'Unknown Artist'}</p>
                {stats.now_playing_playlist && (
                  <p className="text-purple-400/70 text-xs mt-1 truncate">Playlist: {stats.now_playing_playlist}</p>
                )}
              </div>
            </div>
            {stats.now_playing_duration > 0 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs text-white/40 mb-1">
                  <span>{formatTime(stats.now_playing_elapsed)}</span>
                  <span>{formatTime(stats.now_playing_duration)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-purple-500 transition-all duration-1000" style={{ width: `${Math.min(100, (stats.now_playing_elapsed / stats.now_playing_duration) * 100)}%` }} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-white/50">No track currently playing</p>
        )}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="playing-next-card">
        <div className="flex items-center gap-2 mb-4">
          <SkipForward className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Playing Next</h3>
        </div>
        {stats?.playing_next?.song ? (
          <div className="flex items-center gap-4">
            {stats.playing_next.song.art && <img src={stats.playing_next.song.art} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />}
            <div className="min-w-0">
              <p className="text-white font-medium truncate">{stats.playing_next.song.title || 'Unknown Track'}</p>
              <p className="text-white/50 text-sm truncate">{stats.playing_next.song.artist || 'Unknown Artist'}</p>
              {stats.playing_next.playlist && (
                <p className="text-cyan-400/70 text-xs mt-1 truncate">Playlist: {stats.playing_next.playlist}</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-white/50">No upcoming track</p>
        )}
      </div>
    </div>

    {/* Station Controls */}
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="station-controls">
      <h3 className="text-lg font-semibold text-white mb-4">Station Controls</h3>
      {actionMsg && (
        <div className={`mb-3 px-3 py-2 rounded-xl text-sm font-medium ${actionMsg.type === "success" ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}
          data-testid="action-message">
          {actionMsg.text}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={handleSkipSong} disabled={skipping}
          className="bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-400 py-3 px-4 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="skip-song-btn">
          <SkipForward className="w-5 h-5" /> {skipping ? "Skipping..." : "Skip Song"}
        </button>
        <button onClick={() => setShowMetadataModal(true)}
          className="bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-400 py-3 px-4 rounded-xl transition flex items-center justify-center gap-2"
          data-testid="update-metadata-btn">
          <RefreshCw className="w-5 h-5" /> Update Metadata
        </button>
      </div>
    </div>

    {/* Service Management */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Broadcasting Service (Icecast) */}
      <div className="rounded-2xl overflow-hidden" data-testid="broadcasting-service-card">
        <div className="bg-gradient-to-r from-blue-500 to-blue-400 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">Broadcasting Service</h3>
            <p className="text-white/70 text-sm">Icecast</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${serviceStatus?.frontend_running ? "bg-green-500/30 text-green-100 border border-green-300/40" : "bg-red-500/30 text-red-100 border border-red-300/40"}`}
            data-testid="broadcast-status">
            {serviceStatus?.frontend_running ? "Running" : "Stopped"}
          </span>
        </div>
        <div className="bg-[#0f172a] px-5 py-4 flex flex-wrap gap-3">
          <button onClick={handleGoLive}
            className="flex items-center gap-2 text-blue-300 hover:text-white text-sm font-semibold transition"
            data-testid="broadcast-credentials-btn">
            <Key className="w-4 h-4" /> SHOW CREDENTIALS
          </button>
          <button onClick={() => handleServiceAction("frontend", "restart")}
            disabled={serviceLoading.frontend_restart}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold transition disabled:opacity-40"
            data-testid="broadcast-restart-btn">
            <RefreshCw className={`w-4 h-4 ${serviceLoading.frontend_restart ? "animate-spin" : ""}`} /> RESTART
          </button>
          {serviceStatus?.frontend_running ? (
            <button onClick={() => handleServiceAction("frontend", "stop")}
              disabled={serviceLoading.frontend_stop}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-semibold transition disabled:opacity-40"
              data-testid="broadcast-stop-btn">
              <Square className="w-3.5 h-3.5 fill-current" /> STOP
            </button>
          ) : (
            <button onClick={() => handleServiceAction("frontend", "start")}
              disabled={serviceLoading.frontend_start}
              className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm font-semibold transition disabled:opacity-40"
              data-testid="broadcast-start-btn">
              <Play className="w-4 h-4 fill-current" /> START
            </button>
          )}
        </div>
      </div>

      {/* AutoDJ Service (Liquidsoap) */}
      <div className="rounded-2xl overflow-hidden" data-testid="autodj-service-card">
        <div className="bg-gradient-to-r from-blue-500 to-blue-400 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold text-lg">AutoDJ Service</h3>
            <p className="text-white/70 text-sm">Liquidsoap</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${serviceStatus?.backend_running ? "bg-green-500/30 text-green-100 border border-green-300/40" : "bg-red-500/30 text-red-100 border border-red-300/40"}`}
            data-testid="autodj-status">
            {serviceStatus?.backend_running ? "Running" : "Stopped"}
          </span>
        </div>
        <div className="bg-[#0f172a] px-5 py-4 flex flex-wrap gap-3">
          <button onClick={() => handleServiceAction("backend", "restart")}
            disabled={serviceLoading.backend_restart}
            className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold transition disabled:opacity-40"
            data-testid="autodj-restart-btn">
            <RefreshCw className={`w-4 h-4 ${serviceLoading.backend_restart ? "animate-spin" : ""}`} /> RESTART
          </button>
          {serviceStatus?.backend_running ? (
            <button onClick={() => handleServiceAction("backend", "stop")}
              disabled={serviceLoading.backend_stop}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-semibold transition disabled:opacity-40"
              data-testid="autodj-stop-btn">
              <Square className="w-3.5 h-3.5 fill-current" /> STOP
            </button>
          ) : (
            <button onClick={() => handleServiceAction("backend", "start")}
              disabled={serviceLoading.backend_start}
              className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm font-semibold transition disabled:opacity-40"
              data-testid="autodj-start-btn">
              <Play className="w-4 h-4 fill-current" /> START
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Plan Info */}
    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Your Plan: <span className="capitalize">{currentUser.plan === 'premium' ? 'Premium' : currentUser.plan}</span></h3>
          <p className="text-white/60 text-sm mt-1">
            {currentUser.plan === 'starter' && 'Upgrade to Professional for more features'}
            {currentUser.plan === 'professional' && 'Enjoying premium features'}
            {currentUser.plan === 'premium' && 'Full access to all features'}
          </p>
        </div>
        {currentUser.plan !== 'premium' && (
          <button onClick={() => setShowUpgradeModal(true)} className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-xl transition">Upgrade</button>
        )}
      </div>
    </div>

    {/* Music Licensing Notice */}
    <div data-testid="soundexchange-notice" className="bg-white/5 border border-green-500/20 rounded-2xl p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-green-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
          <AlertCircle className="w-5 h-5 text-green-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-white mb-1">Music Licensing — You're Covered</h3>
          <p className="text-white/50 text-sm leading-relaxed mb-3">
            DJsandMCMedia holds a <strong className="text-white/70">network-level blanket license from ASCAP</strong>. Your channel is covered for ASCAP-licensed music — just keep your metadata accurate and follow DMCA rules. Additional PRO licenses are being added as the network grows.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-green-500/10 text-green-400">
              ASCAP Licensed
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/30">
              BMI — Coming Soon
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/30">
              SESAC — Coming Soon
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/30">
              SoundExchange — Coming Soon
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Upgrade Modal */}
    {showUpgradeModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Upgrade Your Plan</h2>
            <button onClick={() => setShowUpgradeModal(false)} className="text-white/60 hover:text-white p-2"><X className="w-6 h-6" /></button>
          </div>
          <p className="text-white/60 mb-6">Select a plan to upgrade to:</p>
          <div className="space-y-4">
            {currentUser.plan !== 'starter' && currentUser.plan !== 'listener' && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                <div><h3 className="text-white font-semibold">Starter</h3><p className="text-white/60 text-sm">100 Listeners - 96kbps - 5GB Storage</p></div>
                <div className="text-right">
                  <p className="text-white font-bold">$9.99<span className="text-white/60 text-sm">/mo</span></p>
                  <button onClick={() => { setShowUpgradeModal(false); navigate('/#pricing'); }} className="text-purple-400 text-sm hover:text-purple-300">Change Plan</button>
                </div>
              </div>
            )}
            {currentUser.plan !== 'professional' && (
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-xl p-4 flex items-center justify-between relative overflow-hidden">
                <div className="absolute top-2 right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">Popular</div>
                <div><h3 className="text-white font-semibold">Professional</h3><p className="text-white/60 text-sm">500 Listeners - 128kbps - 5GB Storage</p></div>
                <div className="text-right">
                  <p className="text-white font-bold">$29.99<span className="text-white/60 text-sm">/mo</span></p>
                  <button onClick={() => { setShowUpgradeModal(false); navigate('/#pricing'); }} className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-1 rounded-lg text-sm">
                    Upgrade
                  </button>
                </div>
              </div>
            )}
            {currentUser.plan !== 'premium' && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                <div><h3 className="text-white font-semibold">Premium</h3><p className="text-white/60 text-sm">Unlimited Listeners - 320kbps - 5GB Storage - 24/7 Support</p></div>
                <div className="text-right">
                  <p className="text-white font-bold">$79.99<span className="text-white/60 text-sm">/mo</span></p>
                  <button onClick={() => { setShowUpgradeModal(false); navigate('/#pricing'); }} className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-1 rounded-lg text-sm">Upgrade</button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
            <p className="text-yellow-400 text-sm">Contact support for custom premium solutions or volume discounts.</p>
          </div>
        </div>
      </div>
    )}

    {/* Referral Program */}
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="referral-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center">
          <Gift className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-white">Invite Creators, Get Rewarded</h3>
          <p className="text-white/40 text-xs">Share your link — earn 1 free month for every signup</p>
        </div>
      </div>
      {referralData ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm font-mono truncate" data-testid="referral-link">
              {`${window.location.origin}?ref=${referralData.referral_code}`}
            </div>
            <button onClick={copyReferralLink}
              className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 px-4 py-2.5 rounded-xl transition flex items-center gap-1.5 text-sm font-medium flex-shrink-0"
              data-testid="copy-referral-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-white">{referralData.referral_count}</p>
              <p className="text-white/40 text-xs">Creators Referred</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{referralData.reward_months}</p>
              <p className="text-white/40 text-xs">Free Months Earned</p>
            </div>
          </div>
        </>
      ) : (
        <div className="text-white/30 text-sm text-center py-4">Loading referral data...</div>
      )}
    </div>

    {/* Update Metadata Modal */}
    {showMetadataModal && (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="metadata-modal-overlay">
        <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="metadata-modal">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Update Metadata</h2>
            <button onClick={() => setShowMetadataModal(false)} className="text-white/60 hover:text-white p-2 rounded-lg hover:bg-white/5" data-testid="metadata-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3 mb-5 flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-cyan-300/80">Use this form to send a manual metadata update. Note that this will override any existing metadata on the stream.</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">Title <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={metaTitle}
                onChange={e => setMetaTitle(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition"
                placeholder="Song title..."
                data-testid="metadata-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1.5">Artist</label>
              <input
                type="text"
                value={metaArtist}
                onChange={e => setMetaArtist(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500 transition"
                placeholder="Artist name..."
                data-testid="metadata-artist-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 mt-6">
            <button onClick={() => setShowMetadataModal(false)}
              className="bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl transition text-sm font-medium"
              data-testid="metadata-close-btn">
              Close
            </button>
            <button onClick={handleUpdateMetadata} disabled={updating || !metaTitle.trim()}
              className="bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl transition text-sm font-semibold flex items-center gap-2"
              data-testid="metadata-submit-btn">
              <RefreshCw className={`w-4 h-4 ${updating ? "animate-spin" : ""}`} />
              {updating ? "Updating..." : "Update Metadata"}
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};
